import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';
import PublicChrome from './public/PublicChrome';
import BlurFade from './public/motion/BlurFade';
import SeoHead, { breadcrumbJsonLd } from './seo/SeoHead';

interface ServiceProject {
  _id: string;
  title: string;
  slug: string;
  description: string;
  imageUrl: string;
  category: string;
  network: string;
  tags: string[];
  featured: boolean;
}

const ServicesPage: React.FC = () => {
  const [allProjects, setAllProjects] = useState<ServiceProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const res = await axios.get(`${apiUrl}/api/projects?status=active&isPublished=true`);
        if (res.data.success) {
          setAllProjects(res.data.data.projects);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const mainnet = allProjects
    .filter((p) => p.network === 'mainnet' && p.category === 'Services')
    .sort((a, b) => a.title.localeCompare(b.title));
  const testnet = allProjects
    .filter((p) => p.network === 'testnet' && p.category === 'Services')
    .sort((a, b) => a.title.localeCompare(b.title));

  const renderGroup = (title: string, list: ServiceProject[], network: string) => (
    <div className="mb-14">
      <BlurFade>
        <h2 className="dusk-section-title !text-2xl mb-6">{title}</h2>
      </BlurFade>
      {list.length === 0 ? (
        <p className="text-dusk-muted text-sm">No {title.toLowerCase()} services available yet.</p>
      ) : (
        <div
          className="grid gap-2.5 md:gap-3"
          style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
        >
          {list.map((pr, i) => (
            <BlurFade key={pr._id} delay={0.03 * i} className="min-w-0">
              <Link
                to={`/services/${network}/${pr.slug}`}
                className="flex flex-col items-center justify-center gap-1.5 w-full h-full min-h-[6.5rem] px-2 py-3 rounded-xl bg-[#0e0d0c] border border-[rgba(243,235,226,0.05)] no-underline transition-colors hover:border-[rgba(208,0,104,0.35)] hover:bg-[#121010]"
              >
                <img
                  src={getFullImageUrl(pr.imageUrl) || '/images/default-project.png'}
                  alt=""
                  className="h-9 w-9 object-contain rounded-full"
                  onError={handleImageError}
                />
                <span className="text-[0.62rem] font-semibold tracking-[0.12em] uppercase text-dusk-ink/90 text-center leading-tight">
                  {pr.title}
                </span>
              </Link>
            </BlurFade>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <PublicChrome>
        <div className="dusk-container py-24 text-center text-dusk-muted">Loading…</div>
      </PublicChrome>
    );
  }

  return (
    <PublicChrome>
      <SeoHead
        title="Services"
        description="DUSKPROOF infrastructure services across mainnet and testnet — validators, tooling, and ops support."
        path="/services"
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
        ])}
      />
      <section className="dusk-section">
        <div className="dusk-container">
          <BlurFade>
            <h1 className="dusk-page-title text-[clamp(2rem,5vw,3rem)] m-0 mb-10">
              Services
            </h1>
          </BlurFade>

          {renderGroup('Mainnet', mainnet, 'mainnet')}
          {renderGroup('Testnet', testnet, 'testnet')}
        </div>
      </section>
    </PublicChrome>
  );
};

export default ServicesPage;
